import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TS } from "../constants/testIds2";
import { Badge } from "../components/ui/badge";
import { ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function TimesheetApprovals() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("submitted");
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));

  const load = () => {
    const params = { status, date_from: dateFrom, date_to: dateTo };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/timesheets", { params }).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, dateFrom, dateTo, tenantId]);

  const decide = async (id, decision) => {
    try {
      await api.patch(`/timesheets/entries/${id}/decide`, { status: decision });
      toast.success(`Entry ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Timesheet approvals</h1>
        <p className="text-sm text-zinc-500 mt-2">Approve or reject submitted timesheet entries from your team.</p>
      </div>

      <div className="border border-zinc-200 bg-white p-3 rounded-sm flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px] rounded-sm" data-testid="ts-approvals-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm" />
        </div>
        <div className="text-xs text-zinc-500 ml-auto">{rows.length} entries</div>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-sm">
        <table className="hr-table" data-testid={TS.approvalsList}>
          <thead><tr><th>Date</th><th>Employee</th><th>Project</th><th>Hours</th><th>Billable</th><th>Notes</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-sm">Nothing to review.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono-plex text-xs">{r.date}</td>
                <td>
                  <div className="text-sm">{r.employee_name}</div>
                  <div className="text-xs text-zinc-500">{r.employee_code}</div>
                </td>
                <td>
                  <div className="text-sm">{r.project_name}</div>
                  <div className="text-xs font-mono-plex text-zinc-500">{r.project_code}</div>
                </td>
                <td className="font-mono-plex text-xs font-semibold">{r.hours}</td>
                <td>{r.billable ? <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Yes</Badge> : <Badge variant="outline" className="rounded-sm bg-zinc-100 border-zinc-300">No</Badge>}</td>
                <td className="text-xs text-zinc-600 max-w-[220px] truncate">{r.task_notes || "—"}</td>
                <td>
                  <Badge variant="outline" className={`rounded-sm ${{
                    draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
                    submitted: "bg-amber-50 text-amber-800 border-amber-200",
                    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
                    rejected: "bg-red-50 text-red-800 border-red-200",
                  }[r.status] || ""}`}>{r.status}</Badge>
                </td>
                <td>
                  {r.status === "submitted" && (
                    <div className="flex gap-1">
                      <button data-testid={TS.approveBtn(r.id)} onClick={() => decide(r.id, "approved")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"><ThumbsUp size={12} className="inline mr-1" /> Approve</button>
                      <button data-testid={TS.rejectBtn(r.id)} onClick={() => decide(r.id, "rejected")} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm"><ThumbsDown size={12} className="inline mr-1" /> Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
